import DeleteIcon from '@mui/icons-material/Delete'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import { Box, Link, Tooltip, Typography } from '@mui/material'
import { GridCallbackDetails } from '@mui/x-data-grid/models/api'
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel
} from '@mui/x-data-grid-pro'
import React, { FC, ReactNode, useContext, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { IntakeGridState } from '../../config'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { AppRoutes } from '../../models'
import { QueryParamNames } from '../../models/QueryParamNames'
import { muiGridSortModel } from '../../models/validation'
import { AppContext } from '../../providers/AppProvider/AppProvider'
import { useGetIntakeRequestsQuery } from '../../redux/api/customerFormsApi'
import { describeVehicle } from '../../utils'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import LinkList from '../SharedUI/CannedJobs/LinkList'
import DeleteDialog from './DeleteDialog'

interface Row {
  id: string
  customer: string
  vehicle: string
  concern: string
  dateTime: Date
  services: string[]
}

const Listing: FC = () => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { data: intakeRequests, isLoading } = useGetIntakeRequestsQuery({
    authorization: accessToken,
    shopId
  })
  const { gridBoxHeight } = useContext(AppContext)

  const [deleteRequestId, setDeleteRequestId] = useState<string>('')

  const navigate = useNavigate()

  const cols: GridColDef[] = useMemo(
    () => [
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 2
      },
      {
        field: 'vehicle',
        headerName: 'Vehicle',
        flex: 3
      },
      {
        field: 'concern',
        headerName: 'Concern',
        flex: 5
      },
      {
        field: 'dateTime',
        type: 'dateTime',
        headerName: 'Date',
        flex: 2
      },
      {
        field: 'repairOrder',
        type: 'string',
        headerName: 'Repair Order',
        flex: 1.5,
        valueGetter: (params) => String(params.value?.localId || ''),
        renderCell: (params: GridRenderCellParams) =>
          params.value ? (
            <Link
              component={RouterLink}
              variant="body2"
              to={`${AppRoutes.ESTIMATES}/${params.row.repairOrder.id}`}>
              {'#' + params.value}
            </Link>
          ) : (
            <></>
          )
      },
      {
        field: 'services',
        type: 'number',
        headerName: '# Services',
        valueGetter: (params) => params.value.length
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            key="create"
            icon={
              <Tooltip title="Create Repair Order">
                <ReceiptLongIcon />
              </Tooltip>
            }
            label="Create Repair Order"
            onClick={() =>
              navigate(
                `${AppRoutes.NEW_ESTIMATE}?${QueryParamNames.INTAKE_REQUEST_ID}=${params.id}&isRepairOrder=true`
              )
            }
          />,
          <GridActionsCellItem
            key="delete"
            label="Delete"
            icon={
              <Tooltip title="Delete Registration">
                <DeleteIcon />
              </Tooltip>
            }
            onClick={() => setDeleteRequestId(params.id as string)}
          />
        ]
      }
    ],
    [navigate]
  )

  const rows: Row[] | null = useMemo(() => {
    if (!intakeRequests) {
      return null
    }
    if (intakeRequests.length === 0) {
      return []
    }

    return intakeRequests.map(
      ({ entry, customer, vehicle, repairOrder, cannedJobs }) => ({
        id: entry!.id!,
        customer: formatName(customer?.information) || '(Unknown)',
        vehicle: describeVehicle(vehicle?.info) || '(Unknown)',
        repairOrder,
        services: cannedJobs!,
        concern: entry!.info!.concern!,
        dateTime: entry!.info!.dateTime!
      })
    )
  }, [intakeRequests])

  const getDetailPanelContent: (params: GridRowParams<Row>) => ReactNode = (
    params
  ) => (
    <Box>
      <Typography variant="h6" sx={{ ml: 2, mt: 2 }}>
        Requested Services (Canned Jobs)
      </Typography>
      <LinkList ids={params.row.services} />
    </Box>
  )

  // Persist sort model changes to be loaded when the component mounts
  let initialSortModel: GridSortModel = [{ field: 'dateTime', sort: 'desc' }]
  try {
    const initialSortModelJson = localStorage.getItem(IntakeGridState)
    if (initialSortModelJson) {
      const parsedSortModelJson: unknown = JSON.parse(initialSortModelJson)
      muiGridSortModel.validateSync(parsedSortModelJson)
      initialSortModel = parsedSortModelJson as GridSortModel
    }
  } catch (e) {
    console.error(e)
    // Without error checking, component will crash if the local storage item's
    // contents are not JSON-parseable or not in the expected shape
    localStorage.removeItem(IntakeGridState)
  }
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel)
  const onSortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails
  ) => void = (newModel) => {
    setSortModel(newModel)
    localStorage.setItem(IntakeGridState, JSON.stringify(newModel))
  }

  return (
    <Box sx={{ height: gridBoxHeight, width: '100%' }}>
      <DeleteDialog
        requestId={deleteRequestId}
        handleClose={() => setDeleteRequestId('')}
      />
      <DataGridPro
        columns={cols}
        rows={rows || []}
        loading={isLoading}
        density="compact"
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => 300}
        sx={{ border: 'none' }}
      />
    </Box>
  )
}

export default Listing
